import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { PageHeader } from '../../../shared/components/PageHeader';
import { DataTable, type DataTableQuery } from '../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../shared/grid/usePersistedGridState';
import { IncludeInactiveToggle } from '../../../shared/components/IncludeInactiveToggle';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../shared/components/ErrorState';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { formatDocumento } from '../../../shared/utils/document';
import { clientsHooks } from '../hooks';
import { clientsApi } from '../api';
import { ClientFormDialog } from '../ClientFormDialog';
import { ClientAddressesDialog } from '../addresses/ClientAddressesDialog';
import type { ClientDto, ClientListItem } from '../types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'nome',
  sortOrder: 'asc',
};

export function ClientsListPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { query, setQuery, clear } = usePersistedGridState('clientes', DEFAULT_QUERY, {});
  const [editing, setEditing] = useState<ClientDto | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<ClientListItem | null>(null);
  const [addressesFor, setAddressesFor] = useState<ClientListItem | null>(null);

  const listQuery = clientsHooks.useList(query);
  const removeMutation = clientsHooks.useDelete();

  async function openEdit(row: ClientListItem): Promise<void> {
    try {
      setEditing(await clientsApi.get(row.id));
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await removeMutation.mutateAsync(pendingDelete.id);
      toast.success(t('features.clientes.toasts.deleted'));
      setPendingDelete(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<ClientListItem>[] = [
    { field: 'nome', headerName: t('features.clientes.columns.nome'), flex: 1, minWidth: 220 },
    {
      field: 'tipoPessoa',
      headerName: t('features.clientes.columns.tipoPessoa'),
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'PJ' ? 'primary' : 'secondary'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'documento',
      headerName: t('features.clientes.columns.documento'),
      width: 180,
      renderCell: (params) => formatDocumento(params.value as string, params.row.tipoPessoa),
    },
    {
      field: 'telefone',
      headerName: t('features.clientes.columns.telefone'),
      width: 150,
      sortable: false,
    },
    {
      field: 'contato',
      headerName: t('features.clientes.columns.contato'),
      width: 180,
      sortable: false,
    },
    {
      field: 'ativo',
      headerName: t('features.clientes.columns.ativo'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={
            params.value
              ? t('features.clientes.status.active')
              : t('features.clientes.status.inactive')
          }
          color={params.value ? 'success' : 'default'}
          size="small"
          variant={params.value ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'criadoPorNome',
      headerName: t('features.clientes.columns.criadoPorNome'),
      width: 170,
      sortable: false,
    },
    {
      field: 'atualizadoEm',
      headerName: t('features.clientes.columns.atualizadoEm'),
      width: 170,
      valueFormatter: (value: string | null) =>
        value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '—',
    },
    {
      field: 'actions',
      headerName: '',
      width: 180,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('features.clientes.actions.manageAddresses')}>
            <IconButton
              size="small"
              aria-label={t('features.clientes.actions.manageAddressesAria', {
                nome: params.row.nome,
              })}
              onClick={() => setAddressesFor(params.row)}
            >
              <HomeWorkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={t('features.clientes.projetos.actions.openFromGrid', { nome: params.row.nome })}
          >
            <IconButton
              size="small"
              aria-label={t('features.clientes.projetos.actions.openFromGrid', {
                nome: params.row.nome,
              })}
              onClick={() => navigate(`/clientes/${params.row.id}/projetos`)}
            >
              <FolderOpenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              aria-label={t('features.clientes.actions.edit', { nome: params.row.nome })}
              onClick={() => void openEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton
              size="small"
              color="error"
              aria-label={t('features.clientes.actions.delete', { nome: params.row.nome })}
              onClick={() => setPendingDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <>
        <PageHeader title={t('menu.clientsRegistry')} />
        <ErrorState
          description={describeApiError(listQuery.error)}
          onRetry={() => listQuery.refetch()}
        />
      </>
    );
  }

  // Cadastro em tela cheia dentro da área de conteúdo (substitui a lista; o
  // estado da lista — query/filtros — é preservado pois a página segue montada).
  if (editing !== null) {
    return (
      <ClientFormDialog
        open
        client={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />
    );
  }

  return (
    <>
      <PageHeader
        title={t('menu.clientsRegistry')}
        breadcrumbs={[{ label: t('menu.clients') }, { label: t('menu.clientsRegistry') }]}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
            {t('features.clientes.actions.new')}
          </Button>
        }
      />

      <DataTable<ClientListItem>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.clientes.searchPlaceholder')}
        filters={<IncludeInactiveToggle query={query} onChange={setQuery} />}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.clientes.deleteConfirm.title')}
        description={t('features.clientes.deleteConfirm.description', {
          nome: pendingDelete?.nome ?? '',
        })}
        confirmLabel={t('common.delete')}
        loading={removeMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <ClientAddressesDialog
        open={addressesFor !== null}
        clientId={addressesFor?.id ?? null}
        clientName={addressesFor?.nome}
        onClose={() => setAddressesFor(null)}
      />
    </>
  );
}
